import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { cn } from "@/lib/utils";
import { filterTriggerClass } from "./filter-trigger";

/**
 * Filtro `date_range`: el valor viaja en la URL como `desde..hasta` en ms,
 * igual que el rango numérico del server (`value_filters.ts`). Las fechas se
 * guardan como medianoche UTC, así que el día local que elige el picker se
 * traduce a días UTC completos.
 */
export function parseDateRangeValue(raw: unknown): { from?: Date; to?: Date } {
  if (typeof raw !== "string" || !raw.includes("..")) return {};
  const [fromStr, toStr] = raw.split("..");
  const toLocalDay = (s: string) => {
    if (!s) return undefined;
    const d = new Date(Number(s));
    if (Number.isNaN(d.getTime())) return undefined;
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  return { from: toLocalDay(fromStr), to: toLocalDay(toStr) };
}

export function serializeDateRangeValue(from: number, to?: number): string {
  const f = new Date(from);
  const t = new Date(to ?? from);
  const start = Date.UTC(f.getFullYear(), f.getMonth(), f.getDate());
  const end = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999);
  return `${start}..${end}`;
}

const formatShortDate = (date: Date) =>
  date.toLocaleDateString("es", { day: "2-digit", month: "short" });

export function DateRangeFilter({
  name,
  value,
  onChange,
  fullWidth,
}: {
  name: string;
  value: unknown;
  onChange: (value: string) => void;
  fullWidth?: boolean;
}) {
  const { from, to } = parseDateRangeValue(value);
  const label = from
    ? `${formatShortDate(from)}${to && to.getTime() !== from.getTime() ? ` – ${formatShortDate(to)}` : ""}`
    : name;
  return (
    <DateRangePicker
      key={String(value ?? "")}
      onUpdate={({ range }) => onChange(serializeDateRangeValue(range.from, range.to))}
      initialDateFrom={from}
      initialDateTo={to}
      align="start"
      locale="en-GB"
      showCompare={false}
      trigger={
        <Button
          variant="outline"
          className={cn(
            "justify-start",
            filterTriggerClass(!!from),
            fullWidth && "w-full",
          )}
        >
          <Icons.Calendar className="opacity-50" />
          <span className="whitespace-nowrap">{label}</span>
        </Button>
      }
    />
  );
}
